/**
 * CROSSD v3 pool(NonfungiblePositionManager) 전체 출금 트랜잭션의 순수 ABI 인코딩.
 * dapp-ui는 viem/wagmi를 직접 쓰지 못하므로 수동 인코딩한다.
 * 셀렉터/호출 구성은 crossd-frontend `use-position-manager.ts` /
 * `use-remove-liquidity.ts`(remove 100% 경로) 준거.
 * 모든 wei-scale 값은 BigInt로 처리한다 (CLAUDE.md NUMERIC_HANDLING).
 */

/** `decreaseLiquidity((uint256,uint128,uint256,uint256,uint256))` */
const DECREASE_LIQUIDITY_SELECTOR = "0x0c49ccbe";
/** `collect((uint256,address,uint128,uint128))` */
const COLLECT_SELECTOR = "0xfc6f7865";
/** `unwrapWETH9(uint256,address)` */
const UNWRAP_WETH9_SELECTOR = "0x49404b7c";
/** `sweepToken(address,uint256,address)` */
const SWEEP_TOKEN_SELECTOR = "0xdf2ab5bb";
/** `multicall(bytes[])` */
const MULTICALL_SELECTOR = "0xac9650d8";

/** collect의 amount0Max/amount1Max — 미수령분 전부 수령. */
export const MAX_UINT128 = (1n << 128n) - 1n;
/** 트랜잭션 deadline 여유 (crossd와 동일: 10분). */
export const DEADLINE_SECONDS = 600n;

const SLIPPAGE_NUMERATOR = 99n;
const SLIPPAGE_DENOMINATOR = 100n;

/** 시뮬레이션 수량에 고정 1% 슬리피지를 적용한 최소 수령량 (BigInt floor). */
export function applyWithdrawSlippage(amount: bigint): bigint {
  return (amount * SLIPPAGE_NUMERATOR) / SLIPPAGE_DENOMINATOR;
}

export function isSameAddress(a?: string, b?: string): boolean {
  return !!a && !!b && a.toLowerCase() === b.toLowerCase();
}

function uintWord(value: bigint, label: string): string {
  if (value < 0n) throw new Error(`${label} must be non-negative`);
  const hex = value.toString(16);
  if (hex.length > 64) throw new Error(`${label} exceeds uint256`);
  return hex.padStart(64, "0");
}

function addressWord(address: string, label: string): string {
  if (!/^0x[a-fA-F0-9]{40}$/.test(address)) {
    throw new Error(`${label} is not a valid address`);
  }
  return address.toLowerCase().replace(/^0x/, "").padStart(64, "0");
}

export interface DecreaseLiquidityParams {
  tokenId: bigint;
  liquidity: bigint;
  amount0Min: bigint;
  amount1Min: bigint;
  deadline: bigint;
}

export function encodeDecreaseLiquidity(
  p: DecreaseLiquidityParams,
): `0x${string}` {
  return (DECREASE_LIQUIDITY_SELECTOR +
    uintWord(p.tokenId, "tokenId") +
    uintWord(p.liquidity, "liquidity") +
    uintWord(p.amount0Min, "amount0Min") +
    uintWord(p.amount1Min, "amount1Min") +
    uintWord(p.deadline, "deadline")) as `0x${string}`;
}

export interface CollectParams {
  tokenId: bigint;
  recipient: string;
  amount0Max: bigint;
  amount1Max: bigint;
}

export function encodeCollect(p: CollectParams): `0x${string}` {
  return (COLLECT_SELECTOR +
    uintWord(p.tokenId, "tokenId") +
    addressWord(p.recipient, "recipient") +
    uintWord(p.amount0Max, "amount0Max") +
    uintWord(p.amount1Max, "amount1Max")) as `0x${string}`;
}

export function encodeUnwrapWeth9(
  amountMinimum: bigint,
  recipient: string,
): `0x${string}` {
  return (UNWRAP_WETH9_SELECTOR +
    uintWord(amountMinimum, "amountMinimum") +
    addressWord(recipient, "recipient")) as `0x${string}`;
}

export function encodeSweepToken(
  token: string,
  amountMinimum: bigint,
  recipient: string,
): `0x${string}` {
  return (SWEEP_TOKEN_SELECTOR +
    addressWord(token, "token") +
    uintWord(amountMinimum, "amountMinimum") +
    addressWord(recipient, "recipient")) as `0x${string}`;
}

/**
 * `multicall(bytes[])` 동적 인코딩.
 * 레이아웃: [배열 오프셋(0x20)][원소 수 N][원소별 상대 오프셋 ×N]
 * [원소별 (길이 워드 + 32바이트 패딩된 데이터)].
 * 원소 오프셋은 오프셋 테이블 시작 기준 상대값 (첫 원소 = N×32).
 */
export function encodeMulticall(
  calls: readonly `0x${string}`[],
): `0x${string}` {
  if (calls.length === 0) {
    throw new Error("multicall requires at least one call");
  }
  const bodies = calls.map((call) => {
    const raw = call.replace(/^0x/, "");
    if (raw.length % 2 !== 0 || !/^[0-9a-fA-F]*$/.test(raw)) {
      throw new Error("multicall element must be valid hex bytes");
    }
    const padded =
      raw.length % 64 === 0
        ? raw
        : raw.padEnd(raw.length + (64 - (raw.length % 64)), "0");
    return uintWord(BigInt(raw.length / 2), "callLength") + padded.toLowerCase();
  });

  let offset = BigInt(calls.length) * 32n;
  const offsets: string[] = [];
  for (const body of bodies) {
    offsets.push(uintWord(offset, "callOffset"));
    offset += BigInt(body.length / 2);
  }

  return (MULTICALL_SELECTOR +
    uintWord(0x20n, "arrayOffset") +
    uintWord(BigInt(calls.length), "arrayLength") +
    offsets.join("") +
    bodies.join("")) as `0x${string}`;
}

/**
 * `decreaseLiquidity` eth_call 시뮬레이션 결과 `(uint256 amount0, uint256 amount1)`
 * 디코드. 빈 값/짧은 값/비 hex는 null.
 */
export function decodeDecreaseLiquidityResult(
  hex: string | undefined,
): { amount0: bigint; amount1: bigint } | null {
  if (!hex || hex === "0x") return null;
  const raw = hex.replace(/^0x/, "");
  if (raw.length < 128 || !/^[0-9a-fA-F]+$/.test(raw)) return null;
  return {
    amount0: BigInt(`0x${raw.slice(0, 64)}`),
    amount1: BigInt(`0x${raw.slice(64, 128)}`),
  };
}

export interface ComposeV3WithdrawInput {
  tokenId: bigint;
  liquidity: bigint;
  amount0Min: bigint;
  amount1Min: bigint;
  deadline: bigint;
  owner: string;
  positionManagerAddress: string;
  token0: string;
  token1: string;
  wcrossAddress: string;
}

/**
 * 전체 출금 multicall calldata 조립 (crossd remove-liquidity 100% 경로와 동일).
 * - WCROSS 미포함: [decreaseLiquidity, collect(recipient=owner)]
 * - WCROSS 포함:   [decreaseLiquidity, collect(recipient=NPM),
 *                   unwrapWETH9(0, owner), sweepToken(비WCROSS 토큰, 0, owner)]
 *   (unwrap/sweep의 amountMinimum 0 — 슬리피지는 decreaseLiquidity min으로 보장)
 */
export function composeV3WithdrawCalldata(
  input: ComposeV3WithdrawInput,
): `0x${string}` {
  const token0IsWcross = isSameAddress(input.token0, input.wcrossAddress);
  const token1IsWcross = isSameAddress(input.token1, input.wcrossAddress);
  const useNativeCross = token0IsWcross || token1IsWcross;

  const calls: `0x${string}`[] = [
    encodeDecreaseLiquidity({
      tokenId: input.tokenId,
      liquidity: input.liquidity,
      amount0Min: input.amount0Min,
      amount1Min: input.amount1Min,
      deadline: input.deadline,
    }),
    encodeCollect({
      tokenId: input.tokenId,
      recipient: useNativeCross ? input.positionManagerAddress : input.owner,
      amount0Max: MAX_UINT128,
      amount1Max: MAX_UINT128,
    }),
  ];

  if (useNativeCross) {
    calls.push(encodeUnwrapWeth9(0n, input.owner));
    calls.push(
      encodeSweepToken(
        token0IsWcross ? input.token1 : input.token0,
        0n,
        input.owner,
      ),
    );
  }

  return encodeMulticall(calls);
}
